import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AcceptInvitation from "@/pages/AcceptInvitation";
import ResetPassword from "@/pages/ResetPassword";
import Verify from "@/pages/Verify";
import Dashboard from "@/pages/Dashboard";
import Samples from "@/pages/Samples";
import SampleDetail from "@/pages/SampleDetail";
import Storage from "@/pages/Storage";
import BoxWorkspace from "@/pages/BoxWorkspace";
import Tests from "@/pages/Tests";
import MyWork from "@/pages/MyWork";
import MyStats from "@/pages/MyStats";
import Schedule from "@/pages/Schedule";
import ReviewQueue from "@/pages/ReviewQueue";
import Inventory from "@/pages/Inventory";
import AIInsights from "@/pages/AIInsights";
import Team from "@/pages/Team";
import Clients from "@/pages/Clients";
import Orders from "@/pages/Orders";
import Catalogue from "@/pages/Catalogue";
import AuditTrail from "@/pages/AuditTrail";
import ComingSoon from "@/pages/ComingSoon";
import Configuration from "@/pages/Configuration";
import Organizations from "@/pages/Organizations";
import Portal from "@/pages/portal/Portal";
import PortalSampleDetail from "@/pages/portal/PortalSampleDetail";
import PortalOrders from "@/pages/portal/PortalOrders";
import NotFound from "@/pages/NotFound";
import { AI_ENABLED } from "@/lib/features";
import { isPortalUser } from "@/lib/reporting";
import { landingPathForUser } from "@/lib/personaNavigation";

function ProtectedRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function InternalRoutes() {
  const { user } = useAuth();
  return isPortalUser(user) ? <Navigate to="/portal" replace /> : <Outlet />;
}

function PersonaLanding() {
  // UX-09/UX-10: each persona lands on its own workspace. The mapping lives
  // in personaNavigation so navigation and landing cannot drift apart.
  const { user } = useAuth();
  return <Navigate to={landingPathForUser(user)} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* FR-18: public certificate verification. Outside the "/" subtree so no
          guard applies, and deliberately not wrapped in PublicRoute — that
          redirects signed-in users away, and a lab checking its own
          certificate while logged in must still see the page its customers
          see. */}
      <Route path="/verify" element={<Verify />} />
      <Route path="/verify/:token" element={<Verify />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="portal" element={<Portal />} />
        <Route path="portal/orders" element={<PortalOrders />} />
        <Route path="portal/samples/:sampleId" element={<PortalSampleDetail />} />
        <Route element={<InternalRoutes />}>
          <Route index element={<PersonaLanding />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="clients" element={<Clients />} />
          <Route path="orders" element={<Orders />} />
          <Route path="samples" element={<Samples />} />
          <Route path="samples/:id" element={<SampleDetail />} />
          <Route path="storage" element={<Storage />} />
          <Route path="storage/boxes/:id" element={<BoxWorkspace />} />
          <Route path="catalogue" element={<Catalogue />} />
          <Route path="configuration" element={<Configuration />} />
          <Route path="tests" element={<Tests />} />
          <Route path="my-work" element={<MyWork />} />
          <Route path="my-stats" element={<MyStats />} />
          <Route path="schedule" element={<Schedule />} />
          <Route path="review" element={<ReviewQueue />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="audit" element={<AuditTrail />} />
          <Route path="ai" element={AI_ENABLED ? <AIInsights /> : <Navigate to="/dashboard" replace />} />
          <Route path="team" element={<Team />} />
          <Route path="organizations" element={<Organizations />} />
          <Route path="qms" element={<ComingSoon />} />
          <Route path="edms" element={<ComingSoon />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="bottom-right" richColors duration={2500} />
      </AuthProvider>
    </div>
  );
}

export default App;
